import {MiniEvent} from "@cdx/common";
import {useSyncExternalStore} from "react";
import type {SprintConfigId} from "../cdx-models/SprintConfig";
import type {MilestoneId} from "../cdx-models/Milestone";
import type {ResolvableEntryReaction} from "../cdx-models/ResolvableEntryReaction";
import type {WithAllFieldsLoaded} from "../cdx-models/utils/MakeModel";
import type {CardOrder} from "../features/card-order/useCardOrder";
import type {VisionBoardId} from "../cdx-models/VisionBoard";
import type {DiscordGuildId} from "../cdx-models/DiscordGuild";
import type {JiraSiteId} from "../cdx-models/JiraSite";
import type {JiraProjectSyncId} from "../cdx-models/JiraProjectSync";
import type {UserId} from "../cdx-models/User";
import type {OrderProp} from "../features/card-order/order-types";
import {ExternalStore} from "./ExternalStore";

type RawStorageMap<T> = {
  storageGet(this: void, key: string): T | undefined;
  storageSet(this: void, key: string, val: T): boolean;
  storageRemove(this: void, key: string): boolean;
};

const fallbackStorage: RawStorageMap<any> = {
  storageGet() {
    return undefined;
  },
  storageSet() {
    return false;
  },
  storageRemove() {
    return false;
  },
};

const getRawStorageMap = <T>(prefix: string, storageGetter: () => Storage): RawStorageMap<T> => {
  const attemptToGetStorage = (): Storage | undefined => {
    try {
      return storageGetter();
    } catch {
      return undefined;
    }
  };

  const storage = attemptToGetStorage();
  if (!storage) return fallbackStorage;

  return {
    storageGet(key) {
      try {
        const content = storage.getItem(`${prefix}${key}`);
        return content ? JSON.parse(content) : undefined;
      } catch {
        return undefined;
      }
    },

    storageSet(key, val) {
      try {
        storage.setItem(`${prefix}${key}`, JSON.stringify(val));
        return true;
      } catch {
        return false;
      }
    },

    storageRemove(key) {
      try {
        storage.removeItem(`${prefix}${key}`);
        return true;
      } catch {
        return false;
      }
    },
  };
};

type RawStorage<T> = {
  get(): T | undefined;
  set(val: T): boolean;
  remove(): boolean;
};

const getRawStorage = <T>(key: string, storageGetter: () => Storage): RawStorage<T> => {
  const attemptToGetStorage = (): Storage | undefined => {
    try {
      return storageGetter();
    } catch {
      return undefined;
    }
  };

  const storage = attemptToGetStorage();
  if (!storage) {
    return {
      get() {
        return undefined;
      },
      set() {
        return false;
      },
      remove() {
        return false;
      },
    };
  }
  return {
    get() {
      try {
        const content = storage.getItem(key);
        return content ? JSON.parse(content) : undefined;
      } catch {
        return undefined;
      }
    },

    set(val) {
      try {
        storage.setItem(key, JSON.stringify(val));
        return true;
      } catch {
        return false;
      }
    },

    remove() {
      try {
        storage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };
};

export const getLocalStorageMap = <T>(prefix: string) =>
  getRawStorageMap<T>(prefix, () => window.localStorage);
export const getSessionStorageMap = <T>(prefix: string) =>
  getRawStorageMap<T>(prefix, () => window.sessionStorage);

export const singleKeyLocalStorage = <T>(key: string) =>
  getRawStorage<T>(key, () => window.localStorage);
export const singleKeySessionStorage = <T>(key: string) =>
  getRawStorage<T>(key, () => window.sessionStorage);

const cdxLocalStorage = getRawStorageMap("", () => window.localStorage);
const cdxSessionStorage = getRawStorageMap("", () => window.sessionStorage);

const getStorageValOrDefaultWithKey = <T>(
  storage: RawStorageMap<T>,
  key: string,
  defaultVal?: T
): {key: string; value: T | null} => {
  if (!key) return {key, value: null};
  const storageVal = storage.storageGet(key);
  const value = storageVal === undefined ? defaultVal || null : storageVal;
  return {key, value};
};

export const getLocalStorageVal = <K extends keyof LocalStorageData>(
  key: K,
  defaultVal?: LocalStorageData[K] | null,
  {type = "local"} = {}
): LocalStorageData[K] | null => {
  const storage = (type === "local" ? cdxLocalStorage : cdxSessionStorage) as RawStorageMap<
    LocalStorageData[K]
  >;
  const {value} = getStorageValOrDefaultWithKey(storage, key, defaultVal);
  return value;
};

export const setLocalStorageVal = <K extends keyof LocalStorageData>(
  key: K,
  val: LocalStorageData[K],
  {type = "local"} = {}
) => {
  const storage = (type === "local" ? cdxLocalStorage : cdxSessionStorage) as RawStorageMap<
    LocalStorageData[K]
  >;
  storage.storageSet(key, val);
  storageChangeListener.emit({key, val, by: null});
};

const storageChangeListener = new MiniEvent<{key: string; val: any; by: any}>();

export const addStorageChangeListener = storageChangeListener.addListener;

export type LocalStorageData = Record<string, unknown> & {
  "navigation-timeline-last-type":
    | {type: "milestone"; id: MilestoneId}
    | {type: "sprintConfig"; id: SprintConfigId};
  "default-search-[KEY]": {
    prop: OrderProp;
    isReversed: boolean;
    secondary?: {prop: OrderProp; isReversed: boolean}[] | null;
    isCompact?: boolean;
    hideEmpty?: boolean;
    mode?: "miniCard" | "tableView";
    isLoaded: boolean;
  };
  "last-reactions": WithAllFieldsLoaded<ResolvableEntryReaction>["value"][];
  "subCardGroup-[parentCardId]-[scalar]": boolean;
  "sub-card-order": CardOrder | null;
  "subcards-filter-done-cards": boolean;
  "last-board-position-[boardId]": {x: number; y: number; scale: number};
  "last-visited-vision-boards": VisionBoardId[];
  "colorful-headings": boolean;
  "projects.collapsedSpaces": Record<string, boolean>;
  "discord-last-guild-id": DiscordGuildId;
  "jira-last-site-id": JiraSiteId;
  "jira-last-project-sync-id": JiraProjectSyncId;
  theme: "light" | "dark" | null;
  "browser-support-light-dark": boolean;
  "card-container-width": number;
  "convo-panel-width": number;
  "child-cards-panel-width": number;
  "hide-promo-banner-until": number;
  "hide-promo-modal-until": number;
  "visitorId-[projectId]": {id: UserId; createdAt: string; lastSessionStarted: string};
  "vision-board-local-map": Record<VisionBoardId, {version: string; createdAt: string}>;
  "hand-convo-tab":
    | "conversations"
    | "sprint"
    | "milestone"
    | "dueCards"
    | "bookmarks"
    | "teamHands"
    | "none";
  "openProject.voteHintInfo": {seenIn: Record<string, boolean>};
  "default-attachment-order": "asc" | "desc";
  "time-format": "12h" | "24h" | null;
};

class StorageEntry<T> extends ExternalStore<T | undefined> {
  private snapshot: T | undefined;
  private readonly storage: RawStorageMap<T>;

  constructor(
    private key: string,
    type: "local" | "session"
  ) {
    super();
    this.storage = (type === "local" ? cdxLocalStorage : cdxSessionStorage) as RawStorageMap<T>;
    this.snapshot = this.storage.storageGet(key);
  }

  getSnapshot = () => {
    return this.snapshot;
  };

  set = (
    setter: T | ((prevState: T | undefined) => T),
    {dontPersist}: {dontPersist?: boolean} = {}
  ) => {
    const value = setter instanceof Function ? setter(this.snapshot) : setter;
    this.snapshot = value;
    if (!dontPersist) this.storage.storageSet(this.key, value);
    storageChangeListener.emit({key: this.key, val: value, by: this});
    this.notify();
  };

  updateExternal = (value: T) => {
    this.snapshot = value;
    this.notify();
  };

  clear = () => {
    this.storage.storageRemove(this.key);
    this.snapshot = undefined;
    storageChangeListener.emit({key: this.key, val: undefined, by: this});
    this.notify();
  };
}

class StorageManager {
  private entries = new Map<string, StorageEntry<any>>();

  constructor() {
    storageChangeListener.addListener(({key, val, by}) => {
      const entry = this.entries.get(key);
      if (entry && entry !== by) {
        entry.updateExternal(val);
      }
    });
  }

  getEntry<K extends keyof LocalStorageData>(
    key: K,
    type: "local" | "session"
  ): StorageEntry<LocalStorageData[K]> {
    let entry = this.entries.get(key);
    if (!entry) {
      entry = new StorageEntry(key, type);
      this.entries.set(key, entry);
    }
    return entry;
  }
}

const storageManager = new StorageManager();

export const useLocalStorageState = <K extends keyof LocalStorageData, Fallback = undefined>(
  key: K,
  defaultVal?: Fallback,
  {type = "local"}: {type?: "local" | "session"} = {}
) => {
  if (process.env.NODE_ENV !== "production" && key === undefined) {
    throw new Error("please pass key to 'useLocalStorageState'");
  }
  const storageEntry = storageManager.getEntry(key, type);
  const val = useSyncExternalStore(storageEntry.subscribe, storageEntry.getSnapshot);
  return [
    (val === undefined ? defaultVal : val) as LocalStorageData[K] | Fallback,
    storageEntry.set,
    {clear: storageEntry.clear},
  ] as const;
};
